import React, { useCallback } from 'react';

import { createStyles, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BillingPlan, SalesPortalPackageRetrieve } from 'types';
import { preserveQueryParams } from 'utils/navigation';

import PackageSummary from './PackageSummary';

const useStyles = createStyles(theme => ({
  billingPlans: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    [theme.fn.smallerThan('xs')]: {
      fontSize: theme.fontSizes.xs,
    },
  },
}));

const SalesPortalPackage = ({
  packageData,
}: {
  packageData: SalesPortalPackageRetrieve | undefined;
}): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { classes, theme } = useStyles();
  const { formatCurrency } = useLocalization(packageData?.gym?.country);

  const handleBillingPlanSelect = useCallback(
    (bp: BillingPlan): void => {
      const path = `${bp.billingPlanId}`;
      const pathWithQueryParams = preserveQueryParams(path, searchParams);
      navigate(pathWithQueryParams);
    },
    [navigate, searchParams],
  );

  return (
    <Stack spacing='lg' px='lg'>
      {packageData && <PackageSummary pckg={packageData} hasBackButton />}

      <Title order={4} fw={600} w='100%'>
        Choose Your Billing Plan
      </Title>

      <Stack>
        {packageData?.billingPlans.map((bp, i) => (
          <Paper
            sx={{
              '&:hover': {
                backgroundColor: theme.colors.gray[0],
                cursor: 'pointer',
              },
            }}
            shadow='xs'
            radius='md'
            withBorder
            p='xs'
            key={i}
            onClick={() => handleBillingPlanSelect(bp)}
          >
            <div className={classes.billingPlans}>
              <Group noWrap spacing='xs'>
                <IconCreditCard />
                <Title order={3} fw={600} truncate>
                  {bp.name}
                </Title>
              </Group>
              <Text color='dark.3' truncate>
                {bp.numberOfInstallments} Installment{bp.numberOfInstallments > 1 && 's'}
                {bp.installmentInterval && (
                  <>
                    {' • '}
                    {bp.installmentIntervalCount} {bp.installmentInterval}
                    {bp.installmentIntervalCount && bp.installmentIntervalCount > 1 && 's'}
                  </>
                )}
              </Text>
              <Title order={3} fw={600} truncate>
                {formatCurrency((bp.totalPrice || 0) / 100)}
              </Title>
            </div>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
};

export default SalesPortalPackage;
